import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn, RouterStateSnapshot } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { ENVIRONMENT } from '../../../../environments/environment.interface';
import { ApiResponse, Metadata } from '../../../api/api-types/api-response.api';
import { Contact, ContactValidDateRange } from '../../../api/app-types/contact.app';
import { ClientDocumentsResponse, DOCUMENT_CATEGORY, DocumentEntry } from '../../../api/app-types/document.app';
import { CONTACT_API } from '../../../api/services/contact.service';
import { VALIDATION_SERVICE } from '../../../api/services/validation.service';
import { REPRESENTATION_MODE } from '../../../core/services/representation-mode.service';
import { formatDateToDisplay } from '../../helpers/date.func';
import { LOGGER } from '../../logging/log.service';
import { PaginatedResponse } from '../../pagination/paginated-response';
import { LocalStorageService } from '../../storage/local-storage.service';

const resolveInitialPageSize = (
	storage: LocalStorageService,
	auth: MsalService | null,
	preferenceKey: string,
	defaultPageSize: number,
	pageSizeOptions: number[],
) => {
	const userId = storage.resolveCurrentUserId(auth);
	if (!userId) return defaultPageSize;

	const saved = storage.getPageSize(userId, preferenceKey);
	return saved && pageSizeOptions.includes(saved) ? saved : defaultPageSize;
};

export const contactResolver: ResolveFn<ApiResponse<Contact, Metadata>> = async (
	_route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const api = inject(CONTACT_API);
	const clientId = Number(_route.paramMap.get('id')!);
	const assignmentId = Number(_route.paramMap.get('assignmentId')!);
	const contactId = Number(_route.paramMap.get('contactId')!);

	const response = await api.getSingle(clientId, assignmentId, contactId);
	return response;
};

export const contactsResolver: ResolveFn<Contact[]> = async (
	_route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const api = inject(CONTACT_API);
	const env = inject(ENVIRONMENT);
	const logger = inject(LOGGER);
	const clientId = Number(_route.paramMap.get('id')!);
	const assignmentId = Number(_route.paramMap.get('assignmentId')!);
	logger.log(`Fetching all contacts for assignment ID: ${assignmentId}`);
	const response = await api.getAll(clientId, assignmentId, 0, env.pagination.range);
	return response.contacts;
};

export const validContactDateRangeResolver: ResolveFn<ContactValidDateRange> = async (
	route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const api = inject(VALIDATION_SERVICE);
	if (!route.paramMap.get('id') || !route.paramMap.get('assignmentId')) {
		throw new Error('Client ID or Assignment ID not provided');
	}
	const clientId = Number(route.paramMap.get('id')!);
	const assignmentId = Number(route.paramMap.get('assignmentId')!);
	const response = await api.getValidDateRangeForContactOfAssignment(clientId, assignmentId);
	return response;
};

export const dashboardContactsResolver: ResolveFn<PaginatedResponse<Contact>> = async (
	_route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const api = inject(CONTACT_API);
	const env = inject(ENVIRONMENT);
	const storage = inject(LocalStorageService);
	const auth = inject(MsalService, { optional: true });

	const representationMode = inject(REPRESENTATION_MODE);
	const userId = representationMode.isActive() ? representationMode.mode().representedUserId : undefined;
	const initialRange = resolveInitialPageSize(
		storage,
		auth,
		'network-contact-overview',
		env.defaultPageSize,
		env.pageSizeOptions,
	);

	const response = await api.getDashboardContacts(env.pagination.offset, initialRange, userId);
	return response;
};

export const networkContactResolver: ResolveFn<Contact> = async (
	_route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const api = inject(CONTACT_API);
	const contactId = Number(_route.paramMap.get('contactId')!);

	const response = await api.getNetworkContact(contactId);
	return response;
};

export const networkContactDocumentEntryResolver: ResolveFn<ClientDocumentsResponse> = async (
	_route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const api = inject(CONTACT_API);
	const contactId = Number(_route.paramMap.get('contactId')!);

	const contact = await api.getNetworkContact(contactId);
	const createdAt = contact.contactedAt ?? new Date();
	const entryName = 'Netzwerkkontakt am ' + formatDateToDisplay(createdAt);

	const documentEntry: DocumentEntry = {
		id: contact.id,
		name: entryName,
		category: DOCUMENT_CATEGORY.CONTACT,
		createdAt: createdAt,
		externalFileReference: contact.documents ?? [],
		isReport: false,
		isFrozen: contact.isFrozen || (contact.editableForDays !== undefined && contact.editableForDays <= 0),
	};

	return {
		commonEntries: [],
		entries: {
			totalEntities: 1,
			range: 10,
			offset: 0,
			entities: [documentEntry],
		},
	};
};
